import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
    REQUEST_CONFIGS_KEYS,
    REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import * as utilsList from '../__index.utils'
import { CheckboxList, RadioList } from '@utils/request'
import * as pageUtils from '../../__forms.utils'

export const STEP6FIELDS = {
    isWaterAndSewerConnectionRequired: 'isWaterAndSewerConnectionRequired',
    isPropertyConnectedToMunicipalConduits:
        'isPropertyConnectedToMunicipalConduits',
    additionalInfoQ42: 'additionalInfoQ42',
    plannedWork: 'plannedWork',
    additionalInfoQ44: 'additionalInfoQ44',
    entrepreneurUnknown: 'entrepreneurUnknown',
    entrepreneurName: 'entrepreneurName',
    entrepreneurRBQNumber: 'entrepreneurRBQNumber',
    entrepreneurRepresentativeName: 'entrepreneurRepresentativeName',
    entrepreneurRepresentativePhoneNumber:
        'entrepreneurRepresentativePhoneNumber',
    entrepreneurRepresentativeEmail: 'entrepreneurRepresentativeEmail',
    entrepreneurRepresentativeAddress: 'entrepreneurRepresentativeAddress',
    entrepreneurRepresentativeCity: 'entrepreneurRepresentativeCity',
    entrepreneurRepresentativePostalCode: 'entrepreneurRepresentativePostalCode'
} as const

export type Step6Fields = keyof typeof STEP6FIELDS

/**
 * useStep6State - Custom hook to manage the state for step 5 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 5.
 */

export function useStep6State() {
    const answers = useAppSelector((state) => state.request.configs)
    const { pageAssets } = requestForm()

    const radioListWhoWillBeInChargeOfWork: RadioList[] =
        utilsList.radioListWhoWillBeInChargeOfWork(pageAssets)
    const radioListYesNo: RadioList[] = utilsList.radioListYesNo(pageAssets)

    const listPlannedWork: CheckboxList[] = pageUtils.listPlannedWork(pageAssets)

    const isEntrepreneurFieldsRequired = (value) => {
        return [
            listPlannedWork[0].value,
            listPlannedWork[1].value,
            listPlannedWork[2].value,
            listPlannedWork[5].value,
            listPlannedWork[6].value
        ].some((option) => value?.toString()?.includes(option))
    }

    //-------------------- Informations sur les travaux INPUTS ------------------------

    // -------------------  DETAILS_5 -------------------
    return {
        // Est-ce que des travaux de branchement d'aquéduc et d'égouts sont nécessaires à vos travaux ?
        isWaterAndSewerConnectionRequired: {
            name: 'isWaterAndSewerConnectionRequired',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_isWaterAndSewerConnectionRequired,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_isWaterAndSewerConnectionRequired',
            value: answers.isWaterAndSewerConnectionRequired?.toString() || '',
            required: true,
            ref: useRef<HTMLSelectElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Votre lot est-il branché aux conduites principales municipales suivantes?
        isPropertyConnectedToMunicipalConduits: {
            name: 'isPropertyConnectedToMunicipalConduits',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_isPropertyConnectedToMunicipalConduits,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_isPropertyConnectedToMunicipalConduits',
            value: answers.isPropertyConnectedToMunicipalConduits || '',
            required: false,
            ref: useRef<HTMLSelectElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Veuillez ajouter des précisions, si nécessaire
        additionalInfoQ42: {
            name: 'additionalInfoQ42',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_additionalInfoQ42,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_additionalInfoQ42',
            value: answers.additionalInfoQ42 || '',
            required: false,
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Dans le cadre de vos travaux, prévoyez vous
        plannedWork: {
            name: 'plannedWork',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_plannedWork,
            labelKey: 'request_form_buildingConstructionOrAdditionPermit_plannedWork',
            value: answers.plannedWork || '',
            required: false,
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Veuillez ajouter des précisions sur les travaux
        additionalInfoQ44: {
            name: 'additionalInfoQ44',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_additionalInfoQ44,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_additionalInfoQ44',
            value: answers.additionalInfoQ44 || '',
            required: false,
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Je ne connais pas encore l'entrepreneur
        entrepreneurUnknown: {
            name: 'entrepreneurUnknown',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_entrepreneurUnknown,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_entrepreneurUnknown',
            value: answers.entrepreneurUnknown?.toString() || String(false),
            required: true,
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Nom de l'entreprise
        entrepreneurName: {
            name: 'entrepreneurName',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyNameExecutingWork,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_companyNameExecutingWork',
            value: answers.entrepreneurName || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Numéro de licence RBQ
        entrepreneurRBQNumber: {
            name: 'entrepreneurRBQNumber',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyRBQNumber,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_companyRBQNumber',
            value: answers.entrepreneurRBQNumber || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Nom complet du (de la) représentant(e) ou de la personne contact
        entrepreneurRepresentativeName: {
            name: 'entrepreneurRepresentativeName',
            label:
                pageAssets?.request_form_buildingConstructionOrAdditionPermit_companyRepresentativeName,
            labelKey:
                'request_form_buildingConstructionOrAdditionPermit_companyRepresentativeName',
            value: answers.entrepreneurRepresentativeName || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Téléphone
        entrepreneurRepresentativePhoneNumber: {
            name: 'entrepreneurRepresentativePhoneNumber',
            label: pageAssets?.label_telephone,
            labelKey: 'label_telephone',
            value: answers.entrepreneurRepresentativePhoneNumber || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Courriel
        entrepreneurRepresentativeEmail: {
            name: 'entrepreneurRepresentativeEmail',
            label: pageAssets?.label_email,
            labelKey: 'label_email',
            value: answers.entrepreneurRepresentativeEmail || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Adresse
        entrepreneurRepresentativeAddress: {
            name: 'entrepreneurRepresentativeAddress',
            label: pageAssets?.label_address,
            labelKey: 'label_address',
            value: answers.entrepreneurRepresentativeAddress || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Ville
        entrepreneurRepresentativeCity: {
            name: 'entrepreneurRepresentativeCity',
            label: pageAssets?.label_city,
            labelKey: 'label_city',
            value: answers.entrepreneurRepresentativeCity || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        },

        // Code postal
        entrepreneurRepresentativePostalCode: {
            name: 'entrepreneurRepresentativePostalCode',
            label: pageAssets?.label_postalCode,
            labelKey: 'label_postalCode',
            value: answers.entrepreneurRepresentativePostalCode || '',
            required:
                isEntrepreneurFieldsRequired(answers?.plannedWork) &&
                answers?.entrepreneurUnknown?.toString() == 'false',
            ref: useRef<HTMLInputElement>(null),
            subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_6
        }
    }
}
